import React, { useState, useEffect, useRef, useCallback } from "react";
import EmailService from "../services/EmailService";
import SignatureSetupModal from "./SignatureSetupModal";
import leadIntakeService from "../services/LeadIntakeService";
import UndoSendToast from "./email/UndoSendToast";
import undoSendService from "../services/UndoSendService";
import useInlineCompletion from "../hooks/useInlineCompletion";
import { TabHint } from "./email/InlineSuggestion";
import ScheduleSendModal from "./email/ScheduleSendModal";
import scheduledSendService from "../services/ScheduledSendService";
import "./ComposeEmail.css";

/**
 * ComposeEmail – Superhuman-style with context-aware AI modes
 *   • Empty message → Generate Mode (create from scratch)
 *   • Has content → Edit Mode (modify existing text)
 *   • Always-visible AI icon with smart mode detection
 *   • Mobile-optimized interface
 */
const ComposeEmail = ({ isOpen, onClose, user, onEmailSent, replyToEmail, activeAccountId }) => {
  /*───────────────────────────*/
  /* Form state                */
  /*───────────────────────────*/
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const contentEditableRef = useRef(null);

  /*───────────────────────────*/
  /* Contacts Autocomplete     */
  /*───────────────────────────*/
  const [contacts, setContacts] = useState([]);
  const [showContactSuggestions, setShowContactSuggestions] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const contactsDebounceTimer = useRef(null);

  /*───────────────────────────*/
  /* AI State Management       */
  /*───────────────────────────*/
  const [aiMode, setAiMode] = useState(null); // null | 'generate' | 'edit'
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [customEditPrompt, setCustomEditPrompt] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [showSignatureSetup, setShowSignatureSetup] = useState(false);
  const [signatureSettings, setSignatureSettings] = useState(null);
  const hasCheckedSignature = useRef(false);

  const aiPromptRef = useRef(null);
  const customEditRef = useRef(null);

  /* Subject suggestions */
  const [subjectOpts, setSubjectOpts] = useState([]);
  const bodyTimer = useRef(null);

  /* Undo Send State */
  const [undoSendVisible, setUndoSendVisible] = useState(false);
  const [pendingEmailData, setPendingEmailData] = useState(null);
  const pendingQueueId = useRef(null);
  const UNDO_DELAY_SECONDS = 5;

  /* Ghost Text / Inline Completion State */
  const [plainTextBody, setPlainTextBody] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [ghostTextEnabled, setGhostTextEnabled] = useState(true); // eslint-disable-line no-unused-vars

  /* Schedule Send State */
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingEmail, setSchedulingEmail] = useState(false);

  // Get plain text from HTML body
  const getPlainTextFromHtml = useCallback((html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }, []);

  // API-based ghost text completion
  const fetchGhostTextFromAPI = useCallback(async (currentText) => {
    if (!currentText || currentText.trim().length < 10) return null;
    try {
      const result = await EmailService.getGhostTextSuggestion({
        currentText,
        subject,
        recipient: to,
        isReply: Boolean(replyToEmail),
      });
      return result.suggestion || null;
    } catch {
      return null;
    }
  }, [subject, to, replyToEmail]);

  // Inline completion hook
  const ghostEnabled = ghostTextEnabled && !aiMode && !aiLoading;

  const {
    suggestion: ghostText,
    hasSuggestion: hasGhostText,
    acceptSuggestion,
    dismissSuggestion,
  } = useInlineCompletion({
    text: plainTextBody,
    cursorPosition,
    enabled: ghostEnabled,
    debounceMs: 600,
    onComplete: fetchGhostTextFromAPI,
  });

  /*───────────────────────────*/
  /* Helper Functions          */
  /*───────────────────────────*/
  const hasContent = () => {
    const cleanBody = body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return cleanBody.length > 0;
  };

  const getAiModeForContent = () => {
    return hasContent() ? 'edit' : 'generate';
  };

  // Format AI-generated text for proper display in contentEditable
  const formatAIContent = (content) => {
    if (!content) return '';
    
    // Convert plain text to properly formatted HTML
    return content
      // First, escape any existing HTML to prevent issues
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Convert line breaks to HTML
      .split('\n')
      .map(line => {
        // Handle empty lines
        if (line.trim() === '') {
          return '<div><br></div>';
        }
        // Wrap non-empty lines in div tags
        return `<div>${line}</div>`;
      })
      .join('')
      // Handle multiple consecutive line breaks for proper spacing
      .replace(/(<div><br><\/div>){2,}/g, '<div><br></div><div><br></div>');
  };

  const setCursorToEnd = () => {
    setTimeout(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.focus();
        const range = document.createRange();
        const sel = window.getSelection();
        range.selectNodeContents(contentEditableRef.current);
        range.collapse(false); // false = collapse to end
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }, 0);
  };

  /*───────────────────────*/
  /* Fetch Contacts            */
  /*───────────────────────*/
  const fetchContacts = async (query) => {
    if (!user?.uid || !activeAccountId) {
      console.log('Cannot fetch contacts - no user or account selected');
      return;
    }

    setContactsLoading(true);
    try {
      const response = await EmailService.getContacts(user.uid, activeAccountId, query);
      console.log('Fetched contacts:', response);

      if (response.contacts && response.contacts.length > 0) {
        setContacts(response.contacts);
        setShowContactSuggestions(true);
      } else {
        setContacts([]);
        setShowContactSuggestions(false);
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  // Handle "To" field changes with debounced contact search
  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);

    // Clear previous debounce timer
    if (contactsDebounceTimer.current) {
      clearTimeout(contactsDebounceTimer.current);
    }

    // Hide suggestions if empty
    if (!value || value.length < 2) {
      setShowContactSuggestions(false);
      setContacts([]);
      return;
    }

    // Debounce contact search
    contactsDebounceTimer.current = setTimeout(() => {
      fetchContacts(value);
    }, 300);
  };

  // Handle contact selection
  const handleContactSelect = (contact) => {
    if (contact.name && contact.email) {
      setTo(`${contact.name} <${contact.email}>`);
    } else {
      setTo(contact.email);
    }
    setShowContactSuggestions(false);
    setContacts([]);
  };

  /*───────────────────────────*/
  /* Click Outside Handler     */
  /*───────────────────────────*/
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showContactSuggestions && !event.target.closest('.contact-suggestions-container')) {
        setShowContactSuggestions(false);
      }
    };

    if (showContactSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showContactSuggestions]);

  /*───────────────────────────*/
  /* User Profile Fetching     */
  /*───────────────────────────*/
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;

      try {
        // Try to fetch user profile from Firebase or your user service
        // For now, we'll use basic auth information
        const profile = {
          name: user.displayName || user.name || '',
          email: user.email || '',
          company: user.company || '',
          professionalTitle: user.professionalTitle || user.title || '',
        };
        setUserProfile(profile);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        // Fallback to basic auth info
        setUserProfile({
          name: user.displayName || user.name || '',
          email: user.email || '',
          company: '',
          professionalTitle: '',
        });
      }
    };

    fetchUserProfile();
  }, [user]);

  /*───────────────────────────*/
  /* Check Signature on Open   */
  /*───────────────────────────*/
  useEffect(() => {
    const checkSignatureSettings = async () => {
      if (!isOpen || !user?.uid || hasCheckedSignature.current) return;

      hasCheckedSignature.current = true;

      try {
        const res = await leadIntakeService.getSettings(user.uid);
        setSignatureSettings(res.settings);

        const hasSignature = res.settings?.signature?.name && res.settings?.signature?.email;
        if (!hasSignature) {
          setShowSignatureSetup(true);
        }
      } catch (error) {
        console.error('Failed to check signature settings:', error);
        setShowSignatureSetup(true);
      }
    };

    checkSignatureSettings();
  }, [isOpen, user?.uid]);

  // Reset signature check when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasCheckedSignature.current = false;
    }
  }, [isOpen]);

  /*───────────────────────────*/
  /* Reset on open             */
  /*───────────────────────────*/
  useEffect(() => {
    if (isOpen && replyToEmail) {
      console.log('Reply/Compose Email Object:', replyToEmail);
      
      // Check if this is a direct composition with to, subject, content properties
      if (replyToEmail.to && replyToEmail.subject && replyToEmail.content) {
        setTo(replyToEmail.to);
        setSubject(replyToEmail.subject);
        setBody(replyToEmail.content);
        setShowPlaceholder(false);
        setAiMode(null);
        setAiPrompt("");
        setSelectedText("");
        setCustomEditPrompt("");
        return;
      }
      
      // Standard email reply handling
      let emailAddress = '';
      let cleanSubject = replyToEmail.subject || '';

      if (replyToEmail.sender) {
        const angleEmailMatch = replyToEmail.sender.match(/<([^>]+)>/);
        if (angleEmailMatch) {
          emailAddress = angleEmailMatch[1];
        } else {
          const plainEmailMatch = replyToEmail.sender.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/);
          if (plainEmailMatch) {
            emailAddress = plainEmailMatch[1];
          } else {
            emailAddress = replyToEmail.sender;
          }
        }
      }
      
      if (!emailAddress && replyToEmail.to) {
        const toEmailMatch = replyToEmail.to.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/);
        if (toEmailMatch) {
          emailAddress = toEmailMatch[1];
        } else {
          emailAddress = replyToEmail.to;
        }
      }
      
      if (replyToEmail.subject) {
        const emailInSubject = replyToEmail.subject.match(/\((.*?@.*?)\)/);
        if (emailInSubject) {
          emailAddress = emailInSubject[1];
          cleanSubject = replyToEmail.subject
            .replace(/\s*\(.*?\)\s*$/, '')
            .replace(/^Re:\s+/i, '');
        } else {
          cleanSubject = cleanSubject.replace(/^Re:\s+/i, '');
        }
      }

      setTo(emailAddress);
      setSubject(`Re: ${cleanSubject}`);
      setBody('');
      setShowPlaceholder(true);
      
      // *** QUICK RESPONSE AI AUTO-GENERATION ***
      // Check if this is a quick response that should auto-generate
      if (replyToEmail.isQuickResponse && replyToEmail.aiPrompt) {
        console.log('🚀 Quick response detected, auto-generating with prompt:', replyToEmail.aiPrompt);
        setAiMode('generate');
        setAiPrompt(replyToEmail.aiPrompt);
        setAiLoading(true);
        
        // Auto-trigger AI generation after a brief delay to let UI update
        setTimeout(() => {
          generateDraft(replyToEmail.aiPrompt);
        }, 100);
      } else {
        setAiMode(null);
        setAiPrompt("");
      }
      
      setSelectedText("");
      setCustomEditPrompt("");
    } else if (isOpen) {
      // Handle new email case
      setTo('');
      setSubject('');
      setBody('');
      setShowPlaceholder(true);
      setAiMode(null);
      setAiPrompt("");
      setSelectedText("");
      setCustomEditPrompt("");
    }
  }, [isOpen, replyToEmail]);

  /*───────────────────────────*/
  /* AI Generation Functions   */
  /*───────────────────────────*/
  const generateDraft = async (prompt) => {
    setAiLoading(true);
    try {
      // Prepare sender information from user profile
      const senderInfo = {
        sender_name: userProfile?.name || user?.displayName || user?.name || '',
        sender_email: userProfile?.email || user?.email || '',
        sender_company: userProfile?.company || '',
        sender_title: userProfile?.professionalTitle || '',
      };

      // Prepare recipient information - extract from 'to' field if available
      const recipientInfo = {
        recipient_name: to ? to.split('@')[0].replace(/[._-]/g, ' ') : '',
        recipient_company: to ? to.split('@')[1]?.split('.')[0] : '',
      };

      // Prepare reply context if this is a reply
      const isReply = !!(replyToEmail && replyToEmail.id);
      const replyContext = isReply ? {
        is_reply: true,
        original_email_sender: replyToEmail.sender || '',
        original_email_subject: replyToEmail.subject || '',
        original_email_snippet: replyToEmail.snippet || '',
        thread_id: replyToEmail.threadId || '',
        message_id: replyToEmail.messageId || replyToEmail.id || ''
      } : {};

      const payload = {
        prompt,
        ...senderInfo,
        ...recipientInfo,
        ...replyContext,
        email_type: isReply ? 'reply' : 'new',
        tone: 'professional',
        include_greeting: true,
        include_closing: true,
        include_signature: Boolean(senderInfo.sender_name),
        max_tokens: 500
      };

      console.log('🤖 Generating professional email with payload:', payload);

      const res = await fetch("https://klorah-fast-server-my8l.onrender.com/api/ai-generate-professional-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('AI generation failed:', res.status, errorText);
        throw new Error(`Generation failed: ${res.status} - ${errorText}`);
      }
      
      const result = await res.json();
      const emailContent = result.email_content;

      if (!emailContent) {
        throw new Error('No email content received from AI');
      }

      console.log('✅ AI generated email content:', emailContent);

      // Format the content for proper display
      const formattedContent = formatAIContent(emailContent);

      // Set the generated content
      setBody(formattedContent);
      setShowPlaceholder(false);
      
      // Update contentEditable and position cursor
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = formattedContent;
        setCursorToEnd();
      }
      
      // Close AI mode after generation
      setAiMode(null);
      setAiPrompt("");
      
      // Track generation
      if (window.mixpanel) {
        window.mixpanel.track('ai_professional_email_generated', {
          prompt_length: prompt.length,
          is_reply: isReply,
          has_sender_info: Boolean(senderInfo.sender_name),
          email_type: isReply ? 'reply' : 'new'
        });
      }
      
    } catch (e) {
      console.error('❌ AI generation failed:', e);
      
      // Enhanced error handling with user feedback
      let errorMessage = 'Failed to generate email. Please try again.';
      if (e.message.includes('400')) {
        errorMessage = 'Invalid request. Please check your inputs and try again.';
      } else if (e.message.includes('401') || e.message.includes('403')) {
        errorMessage = 'Authentication error. Please refresh and try again.';
      } else if (e.message.includes('500')) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (e.message.includes('timeout') || e.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      // Show error to user (you might want to use a toast notification here)
      console.warn('User-friendly error message:', errorMessage);
      
      // Track error for analytics
      if (window.mixpanel) {
        window.mixpanel.track('ai_generation_error', {
          error_message: e.message,
          error_type: e.name,
          prompt_length: prompt ? prompt.length : 0,
          is_quick_response: Boolean(replyToEmail && replyToEmail.isQuickResponse),
          has_user_profile: Boolean(userProfile)
        });
      }
      
      // Reset AI mode but keep the prompt so user can retry
      setAiMode(null);
      setAiPrompt("");
    } finally {
      setAiLoading(false);
    }
  };

  /*───────────────────────────*/
  /* AI Editing Functions      */
  /*───────────────────────────*/
  const applyAiEdit = async (action, customInstruction = null) => {
    const textToEdit = selectedText || body;
    if (!textToEdit.trim()) return;

    setAiLoading(true);
    try {
      let endpoint = '';
      let payload = { text: textToEdit };

      switch (action) {
        case 'improve':
          endpoint = '/api/ai-improve';
          payload.instruction = 'improve'; // Required by EditRequest model
          break;
        case 'shorten':
          endpoint = '/api/ai-shorten';
          payload.instruction = 'shorten'; // Required by EditRequest model
          break;
        case 'lengthen':
          endpoint = '/api/ai-lengthen';
          payload.instruction = 'lengthen'; // Required by EditRequest model
          break;
        case 'simplify':
          endpoint = '/api/ai-simplify';
          payload.instruction = 'simplify'; // Required by EditRequest model
          break;
        case 'rewrite':
          endpoint = '/api/ai-rewrite-voice';
          payload.instruction = 'rewrite'; // Required by EditRequest model
          break;
        case 'translate':
          endpoint = '/api/ai-translate';
          payload.instruction = customInstruction || 'Spanish';
          break;
        case 'custom':
          endpoint = '/api/ai-edit';
          payload.instruction = customInstruction;
          break;
        default:
          throw new Error('Unknown edit action');
      }

      const res = await fetch(`https://klorah-fast-server-my8l.onrender.com${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error(`AI edit failed (${res.status}):`, errorText);
        throw new Error(`Edit failed: ${res.status} - ${errorText}`);
      }
      const result = await res.json();
      
      // Get the edited text from the response
      const editedText = result.improved_text || result.shortened_text || result.lengthened_text || 
                        result.simplified_text || result.rewritten_text || result.translated_text || 
                        result.edited_text;

      let newBody;
      if (selectedText) {
        // Replace only the selected text with formatted content
        const formattedEditedText = formatAIContent(editedText);
        newBody = body.replace(selectedText, formattedEditedText);
      } else {
        // Replace the entire body with formatted content
        newBody = formatAIContent(editedText);
      }
      
      setBody(newBody);
      
      // Update contentEditable and position cursor
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = newBody;
        setCursorToEnd();
      }

      // Clear custom prompt after use
      setCustomEditPrompt("");
      
      // Track edit action
      if (window.mixpanel) {
        window.mixpanel.track('ai_edit_applied', {
          action,
          text_length: textToEdit.length,
          has_selection: Boolean(selectedText)
        });
      }

    } catch (e) {
      console.error('❌ AI edit failed:', e);
      
      // Enhanced error handling with user feedback
      let errorMessage = 'Failed to edit text. Please try again.';
      if (e.message.includes('400')) {
        errorMessage = 'Invalid request. Please check your selection and try again.';
      } else if (e.message.includes('401') || e.message.includes('403')) {
        errorMessage = 'Authentication error. Please refresh and try again.';
      } else if (e.message.includes('500')) {
        errorMessage = 'Server error. Please try again in a few moments.';
      } else if (e.message.includes('timeout') || e.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      // Show error to user
      console.warn('User-friendly error message:', errorMessage);
      
      // Track error for analytics
      if (window.mixpanel) {
        window.mixpanel.track('ai_edit_error', {
          error_message: e.message,
          error_type: e.name,
          action: action,
          text_length: textToEdit ? textToEdit.length : 0,
          has_selection: Boolean(selectedText)
        });
      }
      
    } finally {
      setAiLoading(false);
    }
  };

  /*───────────────────────────*/
  /* Text Selection Handling   */
  /*───────────────────────────*/
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    
    if (selectedText && contentEditableRef.current?.contains(selection.anchorNode)) {
      setSelectedText(selectedText);
    } else {
      setSelectedText("");
    }
  };

  /*───────────────────────────*/
  /* AI Mode Handlers          */
  /*───────────────────────────*/
  const handleAiIconClick = () => {
    const targetMode = getAiModeForContent();
    
    if (aiMode === null) {
      // Open the appropriate mode based on content
      setAiMode(targetMode);
      
      if (targetMode === 'generate') {
        // Generate mode - start with blank prompt
        setAiPrompt("");
        setTimeout(() => aiPromptRef.current?.focus(), 100);
      } else {
        // Edit mode - focus on custom edit field
        setCustomEditPrompt("");
        setTimeout(() => customEditRef.current?.focus(), 100);
      }
    } else {
      // Close current mode
      setAiMode(null);
      setAiPrompt("");
      setCustomEditPrompt("");
      setSelectedText("");
    }
  };

  const handleGenerateSubmit = () => {
    if (aiPrompt.trim()) {
      generateDraft(aiPrompt.trim());
    }
  };

  const handleCustomEdit = () => {
    if (customEditPrompt.trim()) {
      applyAiEdit('custom', customEditPrompt.trim());
    }
  };

  /*───────────────────────────*/
  /* Key handling              */
  /*───────────────────────────*/
  const handleKeyDown = (e) => {
    // Tab to accept ghost text suggestion
    if (e.key === 'Tab' && hasGhostText && !e.shiftKey) {
      e.preventDefault();
      const suggestion = acceptSuggestion();
      if (suggestion && contentEditableRef.current) {
        // Insert the suggestion at the current cursor position
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const textNode = document.createTextNode(suggestion);
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // Fallback: append to end
          contentEditableRef.current.innerHTML += suggestion;
        }
        // Update body state
        setBody(contentEditableRef.current.innerHTML);
        setPlainTextBody(getPlainTextFromHtml(contentEditableRef.current.innerHTML));
      }
      return;
    }

    // Cmd/Ctrl + K for AI
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      handleAiIconClick();
      return;
    }

    // ESC to close AI modes or dismiss ghost text
    if (e.key === 'Escape') {
      if (hasGhostText) {
        dismissSuggestion();
        return;
      }
      if (aiMode) {
        e.preventDefault();
        setAiMode(null);
        setAiPrompt("");
        setCustomEditPrompt("");
        setSelectedText("");
      }
    }
  };

  const handleAiPromptKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGenerateSubmit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setAiMode(null);
      setAiPrompt("");
    }
  };

  const handleCustomEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomEdit();
    }
  };

  /*───────────────────────────*/
  /* Send email                */
  /*───────────────────────────*/
  const handleSend = async () => {
    console.log('Starting handleSend...');

    // Check if account is available
    const accountId = replyToEmail?.accountId || activeAccountId;
    if (!accountId) {
      alert("No email account selected. Please connect an email account first.");
      return;
    }

    if (!to || !subject || !body) {
      console.log('Missing required fields:', {
        hasTo: Boolean(to),
        hasSubject: Boolean(subject),
        hasBody: Boolean(body)
      });
      return;
    }

    if (!user) {
      console.log('No user found - cannot send email');
      return;
    }

    const signature = `
    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee;">
      <p style="color: #666; font-size: 12px; margin: 0;">
        Sent with <a href="https://klorah.app" style="color: #FFB27D; text-decoration: none; font-weight: 500;">Klorah</a>
      </p>
    </div>`;

    const isHTMLContent = /<[a-z][\s\S]*>/i.test(body);
    const emailBody = isHTMLContent ? `${body}${signature}` : `<div>${body}${signature}</div>`;

    // IMPROVED THREADING: Properly handle thread and message IDs for threading
    const messageId = replyToEmail && replyToEmail.id ? replyToEmail.id : null;
    const threadId = replyToEmail && replyToEmail.threadId ? replyToEmail.threadId : null;

    // Additional threading debug info
    console.log('Threading info:', {
      isReply: Boolean(replyToEmail),
      originalEmailId: messageId,
      threadId: threadId,
      originalSubject: replyToEmail?.subject,
      newSubject: subject
    });

    // Prepare email data for queue
    const emailData = {
      user_id: user.uid,
      to,
      subject,
      body: emailBody,
      account_id: accountId,
      thread_id: threadId || undefined,
      message_id: messageId || undefined,
      is_reply: Boolean(replyToEmail && messageId)
    };

    console.log('Queuing email with data:', emailData);

    // Store the pending email data for display
    setPendingEmailData({ to, subject });

    // Queue the email with undo capability
    pendingQueueId.current = undoSendService.queueEmail(emailData, UNDO_DELAY_SECONDS * 1000);

    // Show the undo toast
    setUndoSendVisible(true);

    // Close the compose modal
    onClose();
  };

  // Handle undo send
  const handleUndoSend = useCallback(() => {
    if (pendingQueueId.current) {
      const cancelledEmail = undoSendService.cancelSend(pendingQueueId.current);
      if (cancelledEmail) {
        console.log('Email send cancelled:', cancelledEmail);
        // Restore the email data to the form
        // Note: The compose modal is already closed, so we just hide the toast
      }
    }
    setUndoSendVisible(false);
    setPendingEmailData(null);
    pendingQueueId.current = null;
  }, []);

  // Handle send complete (after undo timeout)
  const handleSendComplete = useCallback(() => {
    console.log('Email sent successfully (after undo timeout)');
    setUndoSendVisible(false);
    setPendingEmailData(null);
    pendingQueueId.current = null;

    if (typeof onEmailSent === 'function') {
      onEmailSent({ success: true });
    }
  }, [onEmailSent]);

  // Handle schedule send
  const handleSchedule = useCallback(async (scheduledTime) => {
    if (!user?.uid || !activeAccountId) {
      console.error('Cannot schedule - no user or account');
      return;
    }

    if (!to || !subject || !body) {
      console.error('Cannot schedule - missing required fields');
      return;
    }

    setSchedulingEmail(true);

    try {
      const emailData = {
        to: to.split(',').map(e => e.trim()).filter(Boolean),
        subject,
        body: getPlainTextFromHtml(body),
        htmlBody: body,
        threadId: replyToEmail?.threadId,
        inReplyTo: replyToEmail?.messageId,
        references: replyToEmail?.references,
      };

      await scheduledSendService.scheduleEmail(
        user.uid,
        emailData,
        scheduledTime,
        activeAccountId
      );

      // Show success message
      console.log('Email scheduled for:', scheduledTime);

      // Reset form and close
      setTo('');
      setSubject('');
      setBody('');
      setShowPlaceholder(true);
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
      }

      if (typeof onEmailSent === 'function') {
        onEmailSent({ success: true, scheduled: true, scheduledTime });
      }

      onClose();
    } catch (error) {
      console.error('Failed to schedule email:', error);
    } finally {
      setSchedulingEmail(false);
    }
  }, [user?.uid, activeAccountId, to, subject, body, replyToEmail, getPlainTextFromHtml, onEmailSent, onClose]);

  /* Subject suggestions effect */
  useEffect(() => {
    if (!body || subject) return;
    clearTimeout(bodyTimer.current);
    bodyTimer.current = setTimeout(async () => {
      try {
        const { subjects } = await fetch("https://klorah-fast-server-my8l.onrender.com/api/ai-subject", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt: body.slice(-300) })
        }).then(r => r.json());
        setSubjectOpts(subjects);
      } catch (error) {
        console.error('Failed to fetch subject suggestions:', error);
      }
    }, 3000);
    return () => clearTimeout(bodyTimer.current);
  }, [body, subject]);

  // Handle editor events
  const handleEditorFocus = () => {
    setShowPlaceholder(false);
    if (!body || body === '<br>' || body === '<div><br></div>') {
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
        // Set cursor at the end of content
        setTimeout(() => {
          if (contentEditableRef.current) {
            contentEditableRef.current.focus();
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(contentEditableRef.current);
            range.collapse(false); // false = collapse to end
            sel.removeAllRanges();
            sel.addRange(range);
          }
        }, 0);
      }
    }
  };

  const handleEditorBlur = () => {
    if (!body || body === '<br>' || body === '<div><br></div>') {
      setBody('');
      setShowPlaceholder(true);
    }
  };

  // Update placeholder visibility
  useEffect(() => {
    setShowPlaceholder(!body || body === '<br>' || body === '<div><br></div>' || body === '');
  }, [body]);

  // Sync body content with contentEditable (only when not actively typing)
  useEffect(() => {
    if (contentEditableRef.current && contentEditableRef.current.innerHTML !== body) {
      // Only update if the user is not currently focused on the editor
      if (document.activeElement !== contentEditableRef.current) {
        contentEditableRef.current.innerHTML = body;
      }
    }
  }, [body]);

  // Render only the toast if modal is closed but undo is visible
  if (!isOpen) {
    if (undoSendVisible) {
      return (
        <UndoSendToast
          isVisible={undoSendVisible}
          delaySeconds={UNDO_DELAY_SECONDS}
          onUndo={handleUndoSend}
          onComplete={handleSendComplete}
          recipient={pendingEmailData?.to || ''}
        />
      );
    }
    return null;
  }

  // Show signature setup if needed
  if (showSignatureSetup) {
    return (
      <SignatureSetupModal
        isOpen={true}
        onClose={() => {
          setShowSignatureSetup(false);
          onClose();
        }}
        onComplete={(newSettings) => {
          setSignatureSettings(newSettings);
          setShowSignatureSetup(false);
          // Update user profile with signature info
          setUserProfile(prev => ({
            ...prev,
            name: newSettings.signature?.name || prev?.name,
            company: newSettings.signature?.businessName || prev?.company,
            professionalTitle: newSettings.signature?.title || prev?.professionalTitle,
          }));
        }}
        userId={user?.uid}
        currentSettings={signatureSettings}
        title="Set up your signature"
        subtitle="Before you send emails"
        description="Your emails will include a professional signature. Set it up once and it'll be used everywhere."
        showCancel={true}
        cancelText="Cancel"
        continueText="Save & Compose"
      />
    );
  }

  const isStandardReply = replyToEmail && replyToEmail.id;
  const isNewMessage = !replyToEmail || (replyToEmail && replyToEmail.content);

  /*───────────────────────────*/
  /* UI                        */
  /*───────────────────────────*/
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-end p-4 sm:p-6 bg-black/40 dark:bg-black/60" style={{ backdropFilter: 'blur(4px)' }}>
      <div className="compose-modal-container bg-white dark:bg-[#0a0a0a] w-full max-w-xl rounded-2xl shadow-2xl border border-gray-200 dark:border-[#333] overflow-hidden transform transition-all duration-300 ease-out max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 sm:px-8 py-4 sm:py-6 border-b border-gray-100 dark:border-[#222] bg-white dark:bg-[#0a0a0a] flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-medium tracking-tight text-gray-900 dark:text-white">
              {isStandardReply ? 'Reply' : 'New Message'}
            </h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-full transition-colors duration-200"
            >
              <svg className="w-5 h-5 text-gray-400 dark:text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 sm:p-8 space-y-4 sm:space-y-6">
            {/* To Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500 dark:text-white/50 mb-1">To</label>
              <div className="relative contact-suggestions-container">
                <input
                  type="text"
                  value={to}
                  onChange={handleToChange}
                  onFocus={() => {
                    if (to.length >= 2 && contacts.length > 0) {
                      setShowContactSuggestions(true);
                    }
                  }}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#111] border border-gray-100 dark:border-[#222] rounded-lg focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all duration-200 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/20"
                  placeholder={replyToEmail ? '' : 'recipient@email.com'}
                  readOnly={isStandardReply}
                />
                {isStandardReply && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="text-xs text-gray-400 dark:text-white/30">Reply to</span>
                  </div>
                )}

                {/* Contact Suggestions Dropdown */}
                {showContactSuggestions && !isStandardReply && (
                  <div className="absolute top-full mt-1 w-full bg-white dark:bg-black border border-gray-200 dark:border-[#333] rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                    {contactsLoading ? (
                      <div className="px-4 py-3 text-gray-500 dark:text-white/50 text-sm">
                        Loading contacts...
                      </div>
                    ) : contacts.length > 0 ? (
                      contacts.map((contact, index) => (
                        <button
                          key={index}
                          onClick={() => handleContactSelect(contact)}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors duration-150 border-b border-gray-100 dark:border-[#222] last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              {contact.name && (
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {contact.name}
                                </div>
                              )}
                              <div className={`text-sm ${contact.name ? 'text-gray-500 dark:text-white/50' : 'text-gray-900 dark:text-white'}`}>
                                {contact.email}
                              </div>
                            </div>
                            {contact.source && (
                              <span className="text-xs text-gray-400 dark:text-white/30 ml-2">
                                {contact.source === 'contacts' ? 'Contact' : 'Recent'}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500 dark:text-white/50 text-sm">
                        No contacts found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Subject Field */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-500 dark:text-white/50 mb-1">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-[#111] border border-gray-100 dark:border-[#222] rounded-lg focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all duration-200 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/20"
                placeholder={replyToEmail ? '' : "What's this about?"}
              />
              {!subject && subjectOpts.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {subjectOpts.map(opt => (
                    <button key={opt}
                      onClick={() => {
                        setSubject(opt);
                        if (window.mixpanel) {
                          window.mixpanel.track('subject_suggestion_clicked');
                        }
                      }}
                      className="px-3 py-1 text-xs rounded-full bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10 text-gray-600 dark:text-white/70 transition-colors duration-200">
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Message Field with AI */}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-500 dark:text-white/50">Message</label>
                {selectedText && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-brand bg-brand/10 px-2 py-1 rounded-md">
                      {selectedText.length} chars selected
                    </span>
                  </div>
                )}
              </div>
              
              <div className="relative">
                <div
                  ref={contentEditableRef}
                  contentEditable
                  onInput={(e) => {
                    const html = e.currentTarget.innerHTML;
                    setBody(html);
                    setPlainTextBody(getPlainTextFromHtml(html));
                    // Update cursor position
                    const selection = window.getSelection();
                    if (selection.rangeCount > 0) {
                      const range = selection.getRangeAt(0);
                      const preCaretRange = range.cloneRange();
                      preCaretRange.selectNodeContents(e.currentTarget);
                      preCaretRange.setEnd(range.endContainer, range.endOffset);
                      setCursorPosition(preCaretRange.toString().length);
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={handleEditorFocus}
                  onBlur={handleEditorBlur}
                  onMouseUp={handleTextSelection}
                  onKeyUp={handleTextSelection}
                  className="compose-body-editor w-full px-4 py-3 bg-gray-50 dark:bg-[#111] border border-gray-100 dark:border-[#222] rounded-lg focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-all duration-200 min-h-[180px] sm:min-h-[200px] overflow-auto text-gray-900 dark:text-white"
                />
                {/* Inline Ghost Text - Shows gray suggestion text after cursor */}
                {hasGhostText && (
                  <>
                    {/* Ghost text overlay positioned to align with text */}
                    <div className="absolute top-0 left-0 right-0 px-4 py-3 pointer-events-none whitespace-pre-wrap text-sm leading-relaxed overflow-hidden" style={{ maxHeight: '100%' }}>
                      {/* Invisible text that takes up space of existing content */}
                      <span className="invisible">{plainTextBody}</span>
                      {/* Ghost suggestion in gray */}
                      <span className="text-gray-400 dark:text-[#555555] opacity-60">{ghostText}</span>
                    </div>
                    {/* Tab hint at bottom */}
                    <div className="absolute bottom-2 left-3 flex items-center gap-1.5 pointer-events-none">
                      <TabHint visible={true} />
                      <span className="text-[10px] text-gray-400 dark:text-[#555]">to accept</span>
                    </div>
                  </>
                )}
                {showPlaceholder && (
                  <div 
                    className="absolute top-0 left-0 px-4 py-3 pointer-events-none text-gray-400 dark:text-white/20"
                    onClick={() => {
                      if (contentEditableRef.current) {
                        contentEditableRef.current.focus();
                      }
                    }}
                  >
                    {isStandardReply ? 'Write your reply here...' : 'Write your message here...'}
                  </div>
                )}

                {/* AI Icon - Always Visible - More Subtle Like Mobile */}
                <button
                  onClick={handleAiIconClick}
                  className={`absolute bottom-3 right-3 p-2 rounded-lg transition-all duration-200 ${
                    aiMode 
                      ? 'bg-gray-600 dark:bg-gray-500 text-white shadow-sm' 
                      : 'bg-gray-200/60 dark:bg-gray-600/30 text-gray-600 dark:text-gray-400 hover:bg-gray-300/80 dark:hover:bg-gray-500/50 hover:text-gray-800 dark:hover:text-gray-200'
                  } transform hover:scale-105 active:scale-95`}
                  disabled={aiLoading}
                >
                  {aiLoading ? (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* AI Modes */}
          {aiMode === 'generate' && (
            <div className="mx-4 sm:mx-8 mb-4 sm:mb-6 p-4 bg-gradient-to-r from-brand/5 to-blue-500/5 border border-brand/20 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                {aiLoading ? (
                  <svg className="animate-spin w-5 h-5 text-brand" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-white/80">
                  {aiLoading 
                    ? (replyToEmail && replyToEmail.isQuickResponse 
                        ? `Generating ${replyToEmail.quickResponseType || 'quick'} response...` 
                        : 'Generating with AI...')
                    : 'Generate with AI'
                  }
                </span>
                {aiLoading && (
                  <span className="text-xs text-gray-500 dark:text-white/40 animate-pulse">
                    This may take a few seconds
                  </span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  ref={aiPromptRef}
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={handleAiPromptKeyDown}
                  placeholder="e.g. write a thank you email about the project update"
                  className="flex-1 px-3 py-2.5 bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20"
                />
                <button
                  onClick={handleGenerateSubmit}
                  disabled={!aiPrompt.trim() || aiLoading}
                  className="px-4 py-2.5 bg-brand text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap flex items-center gap-2"
                >
                  {aiLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating...</span>
                    </>
                  ) : (
                    'Generate'
                  )}
                </button>
              </div>
            </div>
          )}

          {aiMode === 'edit' && (
            <div className="mx-4 sm:mx-8 mb-4 sm:mb-6 p-4 bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-200 dark:border-purple-500/20 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                {aiLoading ? (
                  <svg className="animate-spin w-5 h-5 text-purple-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-white/80">
                  {aiLoading 
                    ? `Editing ${selectedText ? 'Selection' : 'Draft'}...`
                    : `Edit ${selectedText ? 'Selection' : 'Draft'}`
                  }
                </span>
                {aiLoading && (
                  <span className="text-xs text-gray-500 dark:text-white/40 animate-pulse">
                    Processing...
                  </span>
                )}
              </div>

              {/* Quick Edit Buttons - Mobile Optimized */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                <button
                  onClick={() => applyAiEdit('improve')}
                  disabled={aiLoading}
                  className="px-3 py-2 text-sm bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <span className="hidden sm:inline">Improve</span>
                  <span className="sm:hidden">Fix</span>
                </button>
                <button
                  onClick={() => applyAiEdit('shorten')}
                  disabled={aiLoading}
                  className="px-3 py-2 text-sm bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <span>Shorter</span>
                </button>
                <button
                  onClick={() => applyAiEdit('lengthen')}
                  disabled={aiLoading}
                  className="px-3 py-2 text-sm bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <span>Longer</span>
                </button>
                <button
                  onClick={() => applyAiEdit('simplify')}
                  disabled={aiLoading}
                  className="px-3 py-2 text-sm bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <span>Simple</span>
                </button>
                <button
                  onClick={() => applyAiEdit('rewrite')}
                  disabled={aiLoading}
                  className="px-3 py-2 text-sm bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <span className="hidden sm:inline">Your Voice</span>
                  <span className="sm:hidden">Voice</span>
                </button>
                <button
                  onClick={() => applyAiEdit('translate', 'Spanish')}
                  disabled={aiLoading}
                  className="px-3 py-2 text-sm bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  <span>Translate</span>
                </button>
              </div>

              {/* Custom Edit */}
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  ref={customEditRef}
                  type="text"
                  value={customEditPrompt}
                  onChange={(e) => setCustomEditPrompt(e.target.value)}
                  onKeyDown={handleCustomEditKeyDown}
                  placeholder="Describe how to edit the text..."
                  className="flex-1 px-3 py-2.5 bg-white dark:bg-[#111] border border-gray-200 dark:border-[#333] rounded-lg text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-400/20"
                />
                <button
                  onClick={handleCustomEdit}
                  disabled={!customEditPrompt.trim() || aiLoading}
                  className="px-4 py-2.5 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100 dark:border-[#222] bg-white dark:bg-[#0a0a0a] flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-400 dark:text-white/30">
            {loading ? 'Sending...' : (
              <div className="flex items-center gap-2 sm:gap-4">
                <span className="hidden sm:inline">Draft saved</span>
                <div className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 text-xs font-mono bg-gray-100 dark:bg-white/10 rounded text-gray-500 dark:text-white/40">⌘K</kbd>
                  <span className="text-xs">AI</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="px-3 sm:px-5 py-2 text-sm font-medium text-gray-500 dark:text-white/50 hover:bg-gray-50 dark:hover:bg-white/5 rounded-lg transition-colors duration-200"
            >
              Cancel
            </button>
            {/* Schedule Send Button */}
            <button
              onClick={() => setShowScheduleModal(true)}
              disabled={loading || schedulingEmail || !to || !subject || !body}
              className="px-3 py-2 text-gray-500 dark:text-white/50 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Schedule send"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              onClick={handleSend}
              disabled={loading || !to || !subject || !body}
              className="px-4 sm:px-6 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:opacity-90 transform active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-sm shadow-brand/10 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="hidden sm:inline">Sending...</span>
                </>
              ) : (
                <>
                  <span>{isStandardReply ? 'Send Reply' : 'Send'}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Schedule Send Modal */}
        <ScheduleSendModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onSchedule={handleSchedule}
        />
      </div>
    </div>
  );
};

export default ComposeEmail;